/**
 * Subtle upgrade teaser component
 * Uses psychological principles to intrigue users without being pushy
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, TrendingUp, Target, Brain, Lock, Eye, EyeOff } from 'lucide-react';
import { MotionDiv } from '../lib/motion-utils';

interface UpgradeTeaseProps {
  context: 'high-tension' | 'streak' | 'pattern-detected' | 'forecast' | 'insight';
  triggerData?: {
    tensionScore?: number;
    streakDays?: number;
    patternName?: string;
    forecastTrend?: 'improving' | 'declining';
  };
}

export function UpgradeTease({ context, triggerData }: UpgradeTeaseProps) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [revealStep, setRevealStep] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState<string | null>(null);

  // Don't show if user dismissed this type recently
  useEffect(() => {
    const dismissed = localStorage.getItem(`tension_dismissed_tease_${context}`);
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        setIsVisible(false);
      }
    }
  }, [context]);

  const handleDismiss = () => {
    localStorage.setItem(`tension_dismissed_tease_${context}`, Date.now().toString());
    setIsVisible(false);
  };

  const handleExplore = () => {
    // Track that user engaged with the tease
    localStorage.setItem('tension_upgrade_interest', 'true');
    navigate('/pricing');
  };

  if (!isVisible) return null;

  const getContent = () => {
    switch (context) {
      case 'high-tension':
        return {
          icon: <Target className="w-5 h-5 text-amber-600" />,
          title: "We detected something",
          subtitle: "Your RTI pattern from the last 3 days shows an interesting trend",
          teaser: `Based on your score of ${triggerData?.tensionScore || '75'}, we've identified 3 specific actions that could shift this dynamic within 48-72 hours. Premium users can see these personalized recommendations.`,
          blurredPreview: "• Strategic restraint: Reduce initiation by...\n• Availability recalibration: Create space by...\n• Emotional neutrality: When they reach out...",
          stat: "87% of Premium users see tension drop within 5 days",
          cta: "See what we found"
        };
      
      case 'streak':
        return {
          icon: <TrendingUp className="w-5 h-5 text-green-600" />,
          title: `${triggerData?.streakDays || 7} days of consistent tracking`,
          subtitle: "You're building valuable data",
          teaser: "You now have enough data for our pattern detection engine. Premium users unlock weekly trend analysis, 72-hour forecasting, and behavioral pattern identification.",
          blurredPreview: "Your Pattern Profile:\n• Attachment Style: Anxious-Preoccupied\n• Primary Trigger: Response latency\n• Recommended Strategy: Strategic restraint",
          stat: "Users with 7+ days see 3× more accurate insights",
          cta: "Unlock your patterns"
        };
      
      case 'pattern-detected':
        return {
          icon: <Brain className="w-5 h-5 text-violet-600" />,
          title: "Pattern recognized",
          subtitle: `We've seen this ${triggerData?.patternName || 'pursuit-withdrawal'} cycle before`,
          teaser: "Based on 10,000+ users, we know what typically happens next in this dynamic—and more importantly, what actions interrupt it most effectively.",
          blurredPreview: "Your Cycle:\nDay 1-2: High initiation → They withdraw\nDay 3-4: You pursue more → Distance grows\nDay 5-7: Peak tension → Crisis point\n\nRecommended Intervention: Strategic...",
          stat: "This pattern resolves in avg 6.2 days with intervention",
          cta: "See the intervention"
        };
      
      case 'forecast':
        return {
          icon: <Sparkles className="w-5 h-5 text-blue-600" />,
          title: "Forecast available",
          subtitle: "We can predict where this is heading",
          teaser: `Based on your current trajectory and behavioral patterns, our forecasting model predicts ${triggerData?.forecastTrend === 'declining' ? 'increasing tension' : 'stabilization'} over the next 72 hours.`,
          blurredPreview: `${triggerData?.forecastTrend === 'declining' ? '📈 Tension likely to peak at 78/100 by Saturday\n⚠️ Critical intervention window: Next 48 hours\n✅ 3 recommended actions to shift trajectory' : '📉 Tension projected to decrease to 42/100\n✓ Current pattern is stabilizing\n→ 2 actions to maintain momentum'}`,
          stat: `${triggerData?.forecastTrend === 'declining' ? '72% accuracy in crisis prediction' : '68% see continued improvement'}`,
          cta: "View forecast"
        };
      
      case 'insight':
        return {
          icon: <Eye className="w-5 h-5 text-pink-600" />,
          title: "Deeper insight available",
          subtitle: "There's more to this score",
          teaser: "Your RTI score tells the surface story. Premium analysis reveals the underlying attachment dynamics, timing patterns, and specific behavioral triggers driving your tension.",
          blurredPreview: "Deep Analysis:\n• You initiate 83% of contact (anxious pattern)\n• Their response time increased 340% this week\n• Emotional density dropped after you...",
          stat: "Premium users understand their patterns 4× faster",
          cta: "See full analysis"
        };
    }
  };

  const content = getContent();
  const previewLines = content.blurredPreview.split('\n');
  const maxRevealSteps = Math.min(3, previewLines.length || 1);
  const revealedLinesCount = Math.max(0, Math.floor((revealStep / maxRevealSteps) * previewLines.length));
  const visiblePreview =
    revealStep >= maxRevealSteps
      ? content.blurredPreview
      : previewLines.slice(0, Math.max(1, revealedLinesCount)).join('\n');
  const goalOptions = [
    { id: 'deescalate', label: 'Lower tension fast' },
    { id: 'clarity', label: 'Get clarity' },
    { id: 'consistency', label: 'Be more consistent' },
  ];

  return (
    <MotionDiv
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="mb-6"
    >
      <Card className="p-6 bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 border-2 border-neutral-200 dark:border-neutral-700 relative overflow-hidden">
        {/* Subtle premium indicator */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-500/10 to-blue-500/10 blur-3xl" />
        
        <div className="relative space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {content.icon}
              <div>
                <h3 className="font-medium text-neutral-900 dark:text-neutral-100">
                  {content.title}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                  {content.subtitle}
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>

          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            {content.teaser}
          </p>

          <div className="space-y-2">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
              Pick your focus so we can personalize your upgrade path:
            </p>
            <div className="flex flex-wrap gap-2">
              {goalOptions.map((goal) => (
                <button
                  key={goal.id}
                  type="button"
                  onClick={() => setSelectedGoal(goal.id)}
                  className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    selectedGoal === goal.id
                      ? 'border-violet-500 bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                      : 'border-neutral-300 bg-white text-neutral-700 hover:border-violet-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300'
                  }`}
                >
                  {goal.label}
                </button>
              ))}
            </div>
          </div>

          {/* Progressive preview reveal - mobile + desktop friendly */}
          <div className="relative">
            <div
              className={`tension-glow bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 transition-all duration-300 ${
                revealStep < maxRevealSteps ? 'blur-[1px]' : 'blur-none'
              }`}
            >
              <pre className="text-xs text-neutral-600 dark:text-neutral-400 font-mono whitespace-pre-wrap">
                {visiblePreview}
              </pre>
            </div>

            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-blue-500 transition-all duration-300"
                  style={{ width: `${Math.min(100, Math.round((revealStep / maxRevealSteps) * 100))}%` }}
                />
              </div>
              {revealStep < maxRevealSteps ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setRevealStep((step) => Math.min(maxRevealSteps, step + 1))}
                  className="text-violet-700 border-violet-300 hover:bg-violet-50 dark:text-violet-300 dark:border-violet-700 dark:hover:bg-violet-900/20"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Reveal clue
                </Button>
              ) : (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  <EyeOff className="w-3 h-3" />
                  Fully revealed
                </span>
              )}
            </div>
          </div>

          {/* Social proof - subtle */}
          <div className="flex items-center justify-between pt-2 border-t border-neutral-200 dark:border-neutral-700">
            <p className="text-xs text-neutral-500 dark:text-neutral-500">
              {content.stat}
            </p>
            <Button 
              onClick={handleExplore}
              variant="ghost"
              size="sm"
              className="text-violet-600 hover:text-violet-700 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950"
            >
              {selectedGoal ? `Unlock for ${goalOptions.find((g) => g.id === selectedGoal)?.label.toLowerCase()}` : content.cta} →
            </Button>
          </div>
        </div>
      </Card>
    </MotionDiv>
  );
}
